import { initCoreRuntime } from '@core/runtime';
import { getCurrentUser, getTierProgress } from '@core/api/users';
import { claimGiftLink } from '@core/api/gifts';
import { telegramGiftLink, telegramInviteLink } from '@core/links';
import { getVenueSlugCached } from './utils/venueSlug';
import { getReferralCode, getStartParamCached } from './utils/parseStartParam';
import { getTelegramToken, reauthenticateTelegram } from './api/telegramAuth';
import { isDataErased } from '@core/utils/dataErasure';

// Тонкий вход mini-app: идентичность из Telegram initData, auth — Bearer-токен
// /auth/telegram, слаг — из start_param. Референс-логика (реферальный код и
// gift-claim из диплинка — по одному разу на сессию) переехала сюда из UserContext.
// Импортируется ПЕРВЫМ в main.tsx — до первого рендера ядра.

function telegramUser() {
  return window.Telegram?.WebApp?.initDataUnsafe?.user ?? null;
}

// Бот платформы для ссылок на подарок (deepLink выдаёт сервер: <slug>--gift<код>).
const GIFT_BOT_USERNAME: string = import.meta.env.VITE_TELEGRAM_BOT_USERNAME || 'LunitLoyaltyBot';

let referralUsed = false;
let giftClaimUsed = false;

initCoreRuntime({
  kind: 'telegram',
  getVenueSlug: getVenueSlugCached,
  getAuthHeaders: (): Record<string, string> => {
    const token = getTelegramToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
  // Сервер определяет клиента только по сессии (K-002): на 401 — повторный вход по initData. После удаления
  // данных (K-025) — нет: повторный вход молча завёл бы новую карточку, пока клиент читает «Данные удалены».
  reauthenticate: () => (isDataErased() ? Promise.resolve(false) : reauthenticateTelegram()),
  // Удаление данных: сессия отозвана — закрываем мини-апп; следующее открытие — новая карточка.
  afterDataErased: () => {
    window.Telegram?.WebApp?.close();
  },
  fetchProfile: async (identity) => {
    // Источник «кто я» — IdentityContext (TelegramContext); initDataUnsafe — только
    // за именами для синка (может быть пуст в dev-фолбэке).
    const telegramId = identity.telegramId;
    if (!telegramId) return null;
    const tg = telegramUser();

    // Реферальный код из start_param — только при первой загрузке сессии
    let referralCode: string | undefined;
    if (!referralUsed) {
      const rc = getReferralCode();
      if (rc) {
        referralCode = rc;
        referralUsed = true;
      }
    }

    const data = await getCurrentUser(telegramId, true, referralCode, {
      firstName: tg?.first_name ?? identity.firstName ?? undefined,
      lastName: tg?.last_name,
      username: tg?.username,
    });

    // Gift-claim из диплинка (fire-and-forget, один раз)
    if (!giftClaimUsed) {
      const { giftClaimCode } = getStartParamCached();
      if (giftClaimCode) {
        giftClaimUsed = true;
        claimGiftLink(giftClaimCode, telegramId)
          .then((result) => {
            localStorage.setItem('pending-gift-claim', JSON.stringify(result));
          })
          .catch((err) => {
            console.warn('[coreRuntime] Gift claim failed:', err);
          });
      }
    }

    return data;
  },
  fetchTierProgress: async (identity) => {
    return identity.telegramId ? getTierProgress(identity.telegramId) : null;
  },
  capabilities: { telegram: true, requestContact: true, game: true, logout: false },
  // Ссылки «поделиться» из мини-аппа ведут в мини-апп (t.me): друг открывает его в Telegram.
  links: {
    invite: (venue, referralCode) => telegramInviteLink(venue, referralCode),
    gift: (_venue, link) => telegramGiftLink(link.deepLink, GIFT_BOT_USERNAME),
  },
  // Окно Telegram «поделиться» — выбор чата (свойство входа: в PWA его нет).
  openShareDialog: (url, text) => {
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
    // openTelegramLink — в WebApp SDK, но не в нашей минимальной типизации window.Telegram.
    const tg = window.Telegram?.WebApp as unknown as { openTelegramLink?: (u: string) => void } | undefined;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(shareUrl);
    } else {
      window.open(shareUrl);
    }
  },
  requestPhoneContact: (onPhone) => {
    const tg = window.Telegram?.WebApp;
    tg?.requestContact((success, event) => {
      // event.response — подписанная Telegram строка контакта: сервер проверяет подпись и что контакт
      // принадлежит Telegram-аккаунту сессии (K-001). responseUnsafe — только для показа/сверки.
      const phone = event?.responseUnsafe?.contact?.phone_number;
      if (success && phone) {
        void onPhone(phone, event?.response);
      }
    });
  },
});
